import { track } from "@amplitude/analytics-react-native";
import { useApolloClient } from "@apollo/client";
import { memo, ReactElement, ReactNode, useMemo } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import { amp } from "src/api/amplitude";
import { CALLER_UPLOADS_SECTION, EDIT_THEME_MENU, GATHERING_QR_CODE } from "src/api/featureFlags";
import LeaveGatheringDocument from "src/api/graphql/__generated__/documents/LeaveGatheringDocument";
import ReportEntityDocument from "src/api/graphql/__generated__/documents/ReportEntityDocument";
import EntityType from "src/api/graphql/__generated__/enums/EntityType";
import GatheringAccessEnum from "src/api/graphql/__generated__/enums/GatheringAccessEnum";
import evictGathering from "src/api/graphql/evictGathering";
import Menu from "src/components/Menu";
import OpacityView from "src/components/OpacityView";
import { SharePayloadProp } from "src/components/ShareMenu";
import { useGatheringStore } from "src/contexts/GatheringContext";
import log from "src/helpers/log";
import useDeleteGathering from "src/hooks/useDeleteGathering";
import useRouter from "src/hooks/useRouter";
import useStyle from "src/hooks/useStyle";
import { useModal } from "src/modals";
import GatheringModal from "src/modals/configs/GatheringModal";
import GlobalModal, { ShareModalPreviewProps } from "src/modals/configs/GlobalModal";
import { color } from "src/styles";
import { paddingSmall, paddingXXL } from "src/styles/spacing";
import { alert, prompt } from "src/swsh-native";
import toast from "src/swsh-native/toast";
import useGatheringQRCode from "../hooks/useGatheringQRCode";
import usePresentAdminPrivacy from "../hooks/usePresentAdminPrivacy";

const shouldShowShareableNotification = false;
const GatheringOptionsButton = ({
	children,
	style,
}: {
	children?: ReactNode;
	style?: StyleProp<ViewStyle>;
}) => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const palette = useGatheringStore((state) => state.palette);
	const access = useGatheringStore((state) => state.access);
	const apolloClient = useApolloClient();
	const { canShareQRCode, handleGetQRCode } = useGatheringQRCode();
	const { theme } = useStyle();
	const { presentModal } = useModal<GatheringModal>();
	const { presentModal: presentGlobalModal } = useModal<GlobalModal>();
	const isEditing = useGatheringStore((state) => state.isEditing);
	const setViewMode = useGatheringStore((state) => state.setViewMode);
	const viewMode = useGatheringStore((state) => state.viewMode);
	const setEditMode = useGatheringStore((state) => state.setEditMode);
	const { handlePresentAdminPrivacy } = usePresentAdminPrivacy({
		amplitudeEvent: amp.gathering.options.adminPrivacyPress,
	});
	const router = useRouter();

	const mainGroup = useMemo<ReactElement[]>(() => {
		const retItems: ReactElement[] = [];
		const isAdminAccessMenu = access === GatheringAccessEnum.Admin;

		if (isAdminAccessMenu) {
			if (!isEditing) {
				retItems.push(
					<Menu.Item
						key="edit"
						onSelect={() => {
							track(amp.gathering.options.editAlbumPress);
							setEditMode("Edit");
						}}
					>
						<Menu.ItemTitle>Edit album</Menu.ItemTitle>
					</Menu.Item>,
				);
			}
			if (EDIT_THEME_MENU) {
				retItems.push(
					<Menu.Item
						key="editTheme"
						onSelect={() => {
							presentModal("GatheringThemeModal", {
								props: {
									gatheringId,
									defaultPalette: palette ?? undefined,
								},
							});
						}}
					>
						<Menu.ItemTitle>Edit theme (Beta)</Menu.ItemTitle>
					</Menu.Item>,
				);
			}
			retItems.push(
				<Menu.Item key="settings" onSelect={handlePresentAdminPrivacy}>
					<Menu.ItemTitle>Settings</Menu.ItemTitle>
				</Menu.Item>,
			);
			if (viewMode !== "Hidden") {
				retItems.push(
					<Menu.Item
						key="hidden"
						onSelect={() => {
							track(amp.gathering.options.hiddenPress);
							setViewMode("Hidden");
						}}
					>
						<Menu.ItemTitle>Hidden</Menu.ItemTitle>
					</Menu.Item>,
				);
			}
		} else {
			retItems.push(
				<Menu.Item
					key="about"
					onSelect={() => {
						track(amp.gathering.options.participantPrivacyPress);
						presentModal("GatheringParticipantPrivacy", {
							props: {
								gatheringId,
							},
							closeOnHookUnmount: false,
						});
					}}
				>
					<Menu.ItemTitle>About</Menu.ItemTitle>
				</Menu.Item>,
			);
		}
		if (CALLER_UPLOADS_SECTION && viewMode !== "AllCaller") {
			retItems.push(
				<Menu.Item
					key="Your uploads"
					onSelect={() => {
						track(amp.gathering.options.callerPhotosPress);
						setViewMode("AllCaller");
					}}
				>
					<Menu.ItemTitle>Your uploads</Menu.ItemTitle>
				</Menu.Item>,
			);
		}
		if (GATHERING_QR_CODE && canShareQRCode && !isEditing) {
			retItems.push(
				<Menu.Item
					key="qrCode"
					onSelect={async () => {
						const qrCodePromise = handleGetQRCode();
						track(amp.gathering.qrCode.optionsInitiate);

						presentGlobalModal("Share", {
							props: {
								header: {
									title: "Album code",
									subTitle: "Friends can scan this code to join",
								},
								preview: qrCodePromise.then(({ qrCodeUrl }) => {
									if (!qrCodeUrl) return undefined;
									return {
										variant: "Image",
										alt: "QR Code",
										src: qrCodeUrl,
										dim: ({ width, height }) => {
											const size = Math.min(width, height) - paddingXXL;
											return {
												width: size,
												height: size,
											};
										},
									} satisfies ShareModalPreviewProps;
								}),
								data: qrCodePromise.then(({ qrCodeUrl }) => {
									if (!qrCodeUrl) return [];
									return [
										{
											type: "Image",
											url: qrCodeUrl,
											successAmplitude: {
												eventName: amp.gathering.qrCode.shareSuccess,
											},
										},
									] satisfies SharePayloadProp["data"];
								}),
							},
						});
					}}
				>
					<Menu.ItemTitle>QR Code</Menu.ItemTitle>
				</Menu.Item>,
			);
		}
		// See viewers
		/*
		retItems.push(
			<Menu.Item
				key="viewers"
				onSelect={() => {
					track(amp.gathering.options.seeViewersPress);
					presentModal("GatheringViewers", {
						props: {
							gatheringId,
							context,
						},
						closeOnHookUnmount: false,
					});
				}}
			>
				<Menu.ItemTitle>See viewers</Menu.ItemTitle>
			</Menu.Item>,
		);
        */
		/*
        // Share album
		retItems.push(
			<Menu.Item
				key="share"
				onSelect={() => {
					track(amp.gathering.options.shareAlbumPress);
					handleShareGathering();
				}}
			>
				<Menu.ItemTitle>Share album</Menu.ItemTitle>
			</Menu.Item>,
		);
        */
		/*
        // Recap
		retItems.push(
			<Menu.Item
				key="recapShare"
				onSelect={() => {
					track(amp.gathering.options.recapSharePress);
					handleGatheringRecapShare();
				}}
			>
				<Menu.ItemTitle>Recap</Menu.ItemTitle>
				{shouldShowShareableNotification && (
					<ItemIcon
						ios={{
							name: "circle.fill", // required
							pointSize: 16,
							weight: "semibold",
							scale: "medium",
							hierarchicalColor: color.Alert[theme],
						}}
						style={{
							position: "absolute",
							right: paddingMedium,
						}}
					>
						<View
							style={{
								width: 12,
								height: 12,
								backgroundColor: color.Alert[theme],
								borderRadius: 6,
							}}
						/>
					</ItemIcon>
				)}
			</Menu.Item>,
		);
        */

		return retItems;
	}, [
		access,
		viewMode,
		canShareQRCode,
		isEditing,
		presentModal,
		gatheringId,
		palette,
		setViewMode,
		handleGetQRCode,
		presentGlobalModal,
		handlePresentAdminPrivacy,
		setEditMode,
	]);

	const resetGatheringStore = useGatheringStore((state) => state.reset);
	const { handleDeleteGathering } = useDeleteGathering();
	const dangerGroup = useMemo<ReactElement[]>(() => {
		const retItems: ReactElement[] = [];
		const isAdminAccessMenu = access === GatheringAccessEnum.Admin;

		// Report album
		if (!isAdminAccessMenu) {
			retItems.push(
				<Menu.Item
					key="report"
					destructive
					onSelect={() => {
						track(amp.gathering.options.reportAlbum.initiate);
						prompt({
							title: "Report Album",
							message:
								"Please tell us why you are reporting this album. Your message is reported directly to swsh.",
							buttons: [
								{
									text: "Cancel",
									style: "cancel",
									onPress: () => {
										track(amp.gathering.options.reportAlbum.cancel);
									},
								},
								{
									text: "Report",
									style: "destructive",
									onPress: async (reason) => {
										track(amp.gathering.options.reportAlbum.confirm);
										try {
											await apolloClient.mutate({
												mutation: ReportEntityDocument,
												variables: {
													input: {
														entityId: gatheringId,
														entityType: EntityType.Gathering,
														reason: reason,
													},
												},
											});
											toast({
												preset: "done",
												title: "Album reported",
												message: "Thank you for your report",
											});
										} catch (err) {
											log.error({ err }, "Error reporting album");
											toast({
												preset: "error",
												title: "Error reporting album",
											});
										}
									},
								},
							],
						});
					}}
				>
					<Menu.ItemTitle>Report album</Menu.ItemTitle>
				</Menu.Item>,
			);
		}

		// Delete album
		if (isAdminAccessMenu) {
			retItems.push(
				<Menu.Item
					key="delete"
					destructive
					onSelect={() => {
						track(amp.gathering.options.deleteAlbum.initiate);
						alert({
							title: "Delete album",
							message:
								"Are you sure you want to delete this album? This action cannot be undone.",
							buttons: [
								{
									text: "Cancel",
									style: "cancel",
									onPress: () => {
										track(amp.gathering.options.deleteAlbum.cancel);
									},
								},
								{
									text: "Delete",
									style: "destructive",
									onPress: async () => {
										track(amp.gathering.options.deleteAlbum.confirm);
										const { success } = await handleDeleteGathering({
											gatheringId,
											deleteOwnPhotos: true,
										});
										if (success) {
											toast({
												preset: "done",
												title: "Album deleted",
											});
										}
										resetGatheringStore();
										router.replace("/home");
									},
								},
							],
						});
					}}
				>
					<Menu.ItemTitle>Delete album</Menu.ItemTitle>
				</Menu.Item>,
			);
		}

		// Leave album
		if (!isAdminAccessMenu) {
			retItems.push(
				<Menu.Item
					key="leave"
					destructive
					onSelect={() => {
						track(amp.gathering.options.leaveAlbum.initiate);
						alert({
							title: "Leave album",
							message: "Are you sure you want to leave this album?",
							buttons: [
								{
									text: "Cancel",
									style: "cancel",
									onPress: () => {
										track(amp.gathering.options.leaveAlbum.cancel);
									},
								},
								{
									text: "Leave",
									style: "destructive",
									onPress: async () => {
										track(amp.gathering.options.leaveAlbum.confirm);
										try {
											await apolloClient.mutate({
												mutation: LeaveGatheringDocument,
												variables: {
													input: {
														gatheringId,
													},
												},
											});
											router.replace("/home");
											evictGathering(apolloClient.cache, gatheringId);
											toast({
												preset: "done",
												title: "You have left the album",
											});
										} catch (err) {
											log.error({ err }, "Error leaving album");
											toast({
												preset: "error",
												title: "Error leaving album",
											});
										}
									},
								},
							],
						});
					}}
				>
					<Menu.ItemTitle>Leave album</Menu.ItemTitle>
				</Menu.Item>,
			);
		}

		return retItems;
	}, [access, gatheringId, handleDeleteGathering, resetGatheringStore, router, apolloClient]);
	return (
		<Menu.Root
			onOpenChange={(isOpen) => {
				if (isOpen) {
					track(amp.gathering.options.openOptions, {
						access,
					});
				}
			}}
		>
			<Menu.Trigger>
				<>
					<View style={style}>{children}</View>
					{shouldShowShareableNotification && (
						<OpacityView
							pointerEvents="none"
							style={{
								position: "absolute",
								top: 0,
								right: -paddingSmall,
								backgroundColor: color.Alert[theme],
								width: 12,
								height: 12,
								borderRadius: 6,
							}}
						/>
					)}
				</>
			</Menu.Trigger>
			<Menu.Content>
				<Menu.Group>{mainGroup}</Menu.Group>
				<Menu.Separator />
				<Menu.Group>{dangerGroup}</Menu.Group>
			</Menu.Content>
		</Menu.Root>
	);
};
export default memo(GatheringOptionsButton);
